import React, { useState } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  InputNumber,
  Select, 
  Row, 
  Col,
  Typography,
  message,
  Popconfirm,
  Tooltip,
  Badge
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  CarOutlined,
  ToolOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Vehicle {
  id: string;
  tenant_id: string;
  license_plate: string;
  brand?: string;
  model?: string;
  year?: number;
  color?: string;
  vin?: string;
  vehicle_type?: string;
  fuel_type?: string;
  transmission?: string;
  capacity_weight?: number;
  capacity_volume?: number;
  fuel_capacity?: number;
  status: 'available' | 'in_use' | 'maintenance' | 'out_of_service';
  last_maintenance?: string;
  next_maintenance?: string;
  mileage: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  assigned_driver_id?: string;
  driver_first_name?: string;
  driver_last_name?: string;
}

interface VehicleFormData {
  license_plate: string;
  brand?: string;
  model?: string;
  year?: number;
  vin?: string;
  capacity_weight?: number;
  capacity_volume?: number;
  fuel_type?: 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'lpg' | 'cng';
  status: 'available' | 'in_use' | 'maintenance' | 'out_of_service';
  mileage?: number;
}

const AdminVehicles: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Récupération des véhicules
  const { data: vehiclesData, isLoading } = useQuery({
    queryKey: ['vehicles', searchText, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (statusFilter) params.append('status', statusFilter);

      const response = await apiClient.get(`/admin/vehicles?${params.toString()}`);
      return response.data;
    },
  });

  const getVehicleStatusColor = (status: string | null | undefined): string => {
    const colors = {
      available: 'green',
      in_use: 'blue',
      maintenance: 'orange',
      out_of_service: 'red',
    };
    return colors[status as keyof typeof colors] || 'default';
  };
  
  const getVehicleStatusText = (status: string | null | undefined): string => {
    const texts = {
      available: 'Disponible',      // Changé
      in_use: 'En utilisation',     // Ajouté
      maintenance: 'Maintenance',
      out_of_service: 'Hors service',
    };
    return texts[status as keyof typeof texts] || status || 'Statut inconnu';
  };

  const vehicles: Vehicle[] = vehiclesData?.vehicles || [];

  // Mutation pour créer un véhicule
  const createVehicleMutation = useMutation({
    mutationFn: async (data: VehicleFormData) => {
      const response = await apiClient.post('/admin/vehicles', data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Véhicule créé avec succès');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la création');
    },
  });

  // Mutation pour modifier un véhicule
  const updateVehicleMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: VehicleFormData }) => {
      const response = await apiClient.put(`/admin/vehicles/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Véhicule modifié avec succès');
      setIsModalVisible(false);
      setEditingVehicle(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la modification');
    },
  });

  // Mutation pour supprimer un véhicule
  const deleteVehicleMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/vehicles/${id}`);
    },
    onSuccess: () => {
      message.success('Véhicule supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    },
  });

  // Gestionnaires d'événements
  const handleCreate = () => {
    setEditingVehicle(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    form.setFieldsValue(vehicle);
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteVehicleMutation.mutate(id);
  };

  const handleSubmit = (values: VehicleFormData) => {
    if (editingVehicle) {
      updateVehicleMutation.mutate({ id: editingVehicle.id, data: values });
    } else {
      createVehicleMutation.mutate(values);
    }
  };

  // Fonction pour déterminer si une maintenance est due
  const isMaintenanceDue = (vehicle: Vehicle) => {
    if (!vehicle.next_maintenance) return false;
    return dayjs(vehicle.next_maintenance).isBefore(dayjs().add(7, 'days'));
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (record: Vehicle) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <CarOutlined style={{ marginRight: '8px' }} />
            {record.license_plate}
            {isMaintenanceDue(record) && (
              <Tooltip title="Maintenance due bientôt">
                <ToolOutlined style={{ color: '#faad14', marginLeft: '8px' }} />
              </Tooltip>
            )}
          </div>
          {(record.brand || record.model) && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.brand} {record.model} {record.year && `(${record.year})`}
            </Text>
          )}
          {record.vin && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              VIN: {record.vin}
            </div>
          )}
        </div>
      ),
    },
    // Then use in the column definition:
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string | null | undefined) => (
        <Tag color={getVehicleStatusColor(status)}>
          {getVehicleStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: 'Actif', value: 'available' },
        { text: 'En utilisation', value: 'in_use' },
        { text: 'Maintenance', value: 'maintenance' },
        { text: 'Hors service', value: 'out_of_service' },
      ],
    },
    {
      title: 'Chauffeur Assigné',
      key: 'driver',
      render: (record: Vehicle) => (
        record.driver_first_name ? (
          <div>
            <Badge status="success" />
            {record.driver_first_name} {record.driver_last_name}
          </div>
        ) : (
          <Text type="secondary">
            <Badge status="default" />
            Non assigné
          </Text>
        )
      ),
    },
    {
      title: 'Spécifications',
      key: 'specs',
      render: (record: Vehicle) => (
        <div>
          {record.capacity_weight && (
            <div style={{ fontSize: '12px' }}>
              <Text type="secondary">Poids max: </Text>
              <Text>{record.capacity_weight } kg</Text>
            </div>
          )}
          {record.capacity_volume && (
            <div style={{ fontSize: '12px' }}>
              <Text type="secondary">Volume max: </Text>
              <Text>{record.capacity_volume } m³</Text>
            </div>
          )}
          {record.fuel_type && (
            <div style={{ fontSize: '12px' }}>
              <Text type="secondary">Carburant: </Text>
              <Text>{record.fuel_type}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Kilométrage',
      dataIndex: 'mileage',
      key: 'mileage',
      render: (mileage: number) => (
        <Text>{mileage?.toLocaleString()} km</Text>
      ),
      sorter: (a: Vehicle, b: Vehicle) => (a.mileage || 0) - (b.mileage || 0),
    },
    {
      title: 'Maintenance',
      key: 'maintenance',
      render: (record: Vehicle) => (
        <div>
          {record.last_maintenance && (
            <div style={{ fontSize: '12px' }}>
              <Text type="secondary">Dernière: </Text>
              <Text>{dayjs(record.last_maintenance).format('DD/MM/YYYY')}</Text>
            </div>
          )}
          {record.next_maintenance && (
            <div style={{ fontSize: '12px' }}>
              <Text type="secondary">Prochaine: </Text>
              <Text style={{ 
                color: isMaintenanceDue(record) ? '#faad14' : 'inherit',
                fontWeight: isMaintenanceDue(record) ? 'bold' : 'normal'
              }}>
                {dayjs(record.next_maintenance).format('DD/MM/YYYY')}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Vehicle) => (
        <Space>
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer ce véhicule ?"
              description="Cette action est irréversible."
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const maintenanceAlerts = vehicles.filter(isMaintenanceDue);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Gestion des Véhicules</Title>
        <p style={{ color: '#666', margin: 0 }}>
          Gérez votre flotte de véhicules
        </p>
      </div>

      {/* Alertes de maintenance */}
      {maintenanceAlerts.length > 0 && (
        <Card 
          style={{ marginBottom: '16px', backgroundColor: '#fffbe6', borderColor: '#faad14' }}
          size="small"
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ToolOutlined style={{ color: '#faad14', marginRight: '8px' }} />
            <Text strong style={{ color: '#d48806' }}>
              {maintenanceAlerts.length} véhicule(s) nécessite(nt) une maintenance prochainement
            </Text>
          </div>
          <div style={{ marginTop: '8px' }}>
            {maintenanceAlerts.map(vehicle => (
              <Tag key={vehicle.id} color="orange" style={{ margin: '2px' }}>
                {vehicle.license_plate}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* Filtres et actions */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Rechercher par plaque, marque..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
            >
              <Option value="">Tous les statuts</Option>
              <Option value="available">Disponible</Option>
              <Option value="in_use">En utilisation</Option>
              <Option value="maintenance">Maintenance</Option>
              <Option value="out_of_service">Hors service</Option>
            </Select>
          </Col>
          <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouveau véhicule
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des véhicules */}
      <Card>
        <Table
          columns={columns}
          dataSource={vehicles}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} véhicules`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={editingVehicle ? 'Modifier le véhicule' : 'Nouveau véhicule'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingVehicle(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'available',
            mileage: 0,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="Plaque d'immatriculation"
                name="license_plate"
                rules={[{ required: true, message: 'Plaque d\'immatriculation requise' }]}
              >
                <Input placeholder="AB-123-CD" />
              </Form.Item>
            </Col>
            <Col span={12}>
            <Form.Item
              label="Statut"
              name="status"
              rules={[{ required: true, message: 'Statut requis' }]}
            >
              <Select>
                <Option value="available">Disponible</Option>
                <Option value="in_use">En utilisation</Option>
                <Option value="maintenance">Maintenance</Option>
                <Option value="out_of_service">Hors service</Option>
              </Select>
            </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item label="Marque" name="brand">
                <Input placeholder="Renault" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Modèle" name="model">
                <Input placeholder="Master" />
              </Form.Item>
            </Col>
            <Col span={8}>
              {/* Année */}
              <Form.Item 
                label="Année" 
                name="year"
                rules={[
                  { type: 'number', min: 1900, max: new Date().getFullYear() + 1, 
                    message: 'Année invalide' }
                ]}
              >
                <InputNumber 
                  placeholder="2020" 
                  style={{ width: '100%' }}
                  min={1900}
                  max={new Date().getFullYear() + 1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Numéro VIN" name="vin">
            <Input placeholder="VF1AA04F0A0123456" maxLength={17} />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col span={8}>
            <Form.Item 
              label="Poids maximum (kg)" 
              name="capacity_weight"
              rules={[{ type: 'number', min: 0, message: 'Poids invalide' }]}
            >
              <InputNumber 
                placeholder="3500" 
                style={{ width: '100%' }}
                min={0}
              />
            </Form.Item>
            </Col>
            <Col span={8}>
            <Form.Item 
              label="Volume maximum (m³)" 
              name="capacity_volume"
              rules={[{ type: 'number', min: 0, message: 'Volume invalide' }]}
            >
              <InputNumber 
                placeholder="15" 
                style={{ width: '100%' }}
                min={0}
                step={0.1}
              />
            </Form.Item>
            </Col>
            <Col span={8}>
            <Form.Item label="Type de carburant" name="fuel_type">
              <Select placeholder="Sélectionner">
                <Option value="diesel">Diesel</Option>
                <Option value="gasoline">Essence</Option>
                <Option value="electric">Électrique</Option>
                <Option value="hybrid">Hybride</Option>
                <Option value="lpg">GPL</Option>
                <Option value="cng">GNC</Option>
              </Select>
            </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            label="Kilométrage actuel" 
            name="mileage"
            rules={[{ type: 'number', min: 0, message: 'Kilométrage invalide' }]}
          >
            <InputNumber 
              placeholder="50000" 
              style={{ width: '100%' }}
              min={0}
            />
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createVehicleMutation.isPending || updateVehicleMutation.isPending}
              >
                {editingVehicle ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminVehicles;